import * as React from "react";
import { useState } from "react";
import { Button, Input, Spinner } from "@fluentui/react-components";

export type MatrixCreds = {
  accessToken: string;
  userId: string;
  homeserverUrl: string;
  deviceId?: string;
  refreshToken?: string;
  homeserverInput?: string;
  identityServerUrl?: string;
  webClientUrl?: string;
  wellKnown?: any;
};

async function fetchWellKnown(homeserver: string): Promise<any> {
  let base = homeserver.replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(base)) base = "https://" + base;
  const url = base + "/.well-known/matrix/client";
  const resp = await fetch(url);
  if (!resp.ok) throw new Error("Failed to fetch .well-known: " + resp.statusText);
  return resp.json();
}

function openLoginDialog(
  urls: { prod: string; dev: string },
  onSuccess: (creds: MatrixCreds) => void
) {
  const isDev = process.env.NODE_ENV === "development" || location.hostname === "localhost";

  const finalUrl = isDev ? urls.dev : urls.prod;

  if (!window.Office || !Office.context?.ui) {
    window.open(finalUrl, "_blank");
    return;
  }

  Office.context.ui.displayDialogAsync(
    finalUrl,
    { height: 70, width: 70, displayInIframe: false },
    (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("Failed to open dialog:", asyncResult.error);
        return;
      }

      const dialog = asyncResult.value;

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
        if ("message" in arg && arg.message) {
          try {
            const data = JSON.parse(arg.message);
            if (data.accessToken && data.userId && data.homeserverUrl) {
              dialog.close();
              onSuccess(data as MatrixCreds);
            }
          } catch (e) {
            console.error("Invalid message from dialog:", e);
          }
        }
      });

      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        dialog.close();
      });
    }
  );
}

const SignIn: React.FC<{ onSuccess: (creds: MatrixCreds) => void }> = ({ onSuccess }) => {
  const [homeserver, setHomeserver] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const wellknown = await fetchWellKnown(homeserver);
      const webClient = "https://chat.ssh.net";
      if (!webClient) throw new Error("m.web_client.base_url is missing from .well-known!");

      const hs = wellknown["m.homeserver"]?.base_url || homeserver;
      const is = wellknown["m.identity_server"]?.base_url || "";

      const qp = new URLSearchParams({
        hs,
        serverName: homeserver,
      });
      if (is) qp.append("is", is);

      const baseProd = webClient.replace(/\/+$/, "") + "/addin-login.html";
      const baseDev = "https://localhost:8080/addin-login.html";

      const prodUrl = `${baseProd}?${qp.toString()}`;
      const devUrl = `${baseDev}?${qp.toString()}`;

      openLoginDialog({ prod: prodUrl, dev: devUrl }, (credsFromDialog) => {
        onSuccess({
          ...credsFromDialog,
          homeserverInput: homeserver,
          webClientUrl: webClient,
          wellKnown: wellknown,
          identityServerUrl: is || undefined,
        });
      });
    } catch (e: any) {
      setError(e.message || "Unknown error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ textAlign: "center", marginTop: 64, paddingLeft: 16, paddingRight: 16 }}>
      <img
        src="https://play-lh.googleusercontent.com/W5YTsvLPcZGSDEX4oxoSZeY6dswMASW2p771cSehs29AwxtVJDmj_WKUSJaYv16YPA"
        alt="SalaX logo"
        style={{ width: 64, height: 64, marginBottom: 24 }}
      />
      <h2>SalaX Secure Messaging</h2>
      <p style={{ fontSize: "14px" }}>
        Easily schedule and manage SalaX meetings directly in Microsoft Outlook.
      </p>
      <h4>Enter your Matrix homeserver to sign in</h4>
      <Input
        placeholder="Enter your homeserver..."
        style={{ width: "100%", marginBottom: 12 }}
        value={homeserver}
        onChange={(_, d) => setHomeserver(d.value)}
        disabled={loading}
        type="url"
      />
      <Button
        appearance="primary"
        style={{ width: "100%", marginBottom: 12 }}
        onClick={handleSignIn}
        disabled={!homeserver || loading}
      >
        {loading ? <Spinner size="tiny" /> : "Sign in"}
      </Button>
      {error && <div style={{ color: "#d13438", marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default SignIn;
